import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useGetMachineryDirectiveCompliance,
  useSetHighRiskUserOverrideReason,
  useUpdatePartlyCompletedMachine,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useGetProduct } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ProductMetadataHighRiskReasonDialog } from '@/features/compliance-studio/machinery-directive/steps/settings/ProductMetadataHighRiskReasonDialog'
import { ProductMetadataMachineClassificationDialog } from '@/features/compliance-studio/machinery-directive/steps/settings/ProductMetadataMachineClassificationDialog'
import { EditProductDialog } from '@/features/product-catalog/components/EditProductDialog'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'

export function ProductMetadataSettingsPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const productId = params.productId ?? ''
  const machineryDirectiveComplianceId =
    params.machineryDirectiveComplianceId ?? ''
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { t: tProductCatalog } = useTranslation('productCatalog')
  const { data: product, isLoading: isLoadingProduct } =
    useGetProduct(productId)
  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false)

  if (!productId) {
    return null
  }

  return (
    <div className='space-y-4'>
      <ComplianceSubPageHeader>
        <div className='space-y-0.5'>
          <ComplianceSubPageTitle>
            {t('settings.productMetadata.pageTitle')}
          </ComplianceSubPageTitle>
          <p className='text-sm text-muted-foreground'>
            {t('settings.productMetadata.pageDescription')}
          </p>
        </div>
      </ComplianceSubPageHeader>
      <ProductMetadataHighRiskAnalysisCard
        complianceId={machineryDirectiveComplianceId}
      />
      <ProductMetadataMachineClassificationCard
        complianceId={machineryDirectiveComplianceId}
      />
      <Card>
        {product != null && (
          <CardHeader className='flex flex-row items-center justify-between gap-4 space-y-0'>
            <CardTitle className='text-base'>
              {tProductCatalog('detail.fields.name')}{' '}
              <span className='font-normal text-muted-foreground'>
                {product.name ?? tProductCatalog('detail.fields.notSet')}
              </span>
            </CardTitle>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setEditProductDialogOpen(true)}
            >
              {t('common:actions.edit')}
            </Button>
          </CardHeader>
        )}
        <CardContent className='space-y-2 text-sm'>
          {isLoadingProduct ? (
            <div className='space-y-2' aria-busy='true'>
              <Skeleton className='h-4 w-2/3' />
              <Skeleton className='h-4 w-full' />
            </div>
          ) : product ? (
            <>
              <p>
                <span className='font-medium text-foreground'>
                  {tProductCatalog('detail.fields.productModel')}{' '}
                </span>
                {product.productModel ??
                  tProductCatalog('detail.fields.notSet')}
              </p>
              <p>
                <span className='font-medium text-foreground'>
                  {tProductCatalog('detail.fields.yearOfManufacture')}{' '}
                </span>
                {product.yearOfManufacture ??
                  tProductCatalog('detail.fields.notSet')}
              </p>
            </>
          ) : (
            <p className='text-muted-foreground'>
              {t('errors.productNotFound')}
            </p>
          )}
        </CardContent>
      </Card>
      {product != null && (
        <EditProductDialog
          product={product}
          open={editProductDialogOpen}
          onOpenChange={setEditProductDialogOpen}
        />
      )}
    </div>
  )
}

function ProductMetadataHighRiskAnalysisCard({
  complianceId,
}: {
  complianceId: string | undefined
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { data: compliance, isLoading } =
    useGetMachineryDirectiveCompliance(complianceId)
  const setHighRiskUserOverrideReasonMutation =
    useSetHighRiskUserOverrideReason(complianceId)
  const [editReasonDialogOpen, setEditReasonDialogOpen] = useState(false)

  if (!complianceId) {
    return null
  }

  const openEditDialog = () => {
    setEditReasonDialogOpen(true)
  }

  const handleSaveHighRiskReason = (reason: string | null) => {
    setHighRiskUserOverrideReasonMutation.mutate(
      { reason },
      {
        onSuccess: (_data, variables) => {
          setEditReasonDialogOpen(false)
          toast.success(
            variables.reason == null
              ? t('settings.productMetadata.highRiskAnalysis.clearSuccess')
              : t('settings.productMetadata.highRiskAnalysis.editSuccess')
          )
        },
        onError: (error) => {
          toast.error(
            error.message ||
              t('settings.productMetadata.highRiskAnalysis.editFailed')
          )
        },
      }
    )
  }

  return (
    <Card>
      <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-4 space-y-0'>
        <div className='flex w-full justify-between gap-2'>
          <div className='space-y-1'>
            <CardTitle>
              {t('settings.productMetadata.highRiskAnalysis.title')}
            </CardTitle>
            <CardDescription>
              {t('settings.productMetadata.highRiskAnalysis.description')}
            </CardDescription>
          </div>
          {compliance?.highRiskCheckedAt != null && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={openEditDialog}
              disabled={setHighRiskUserOverrideReasonMutation.isPending}
            >
              {t('settings.productMetadata.highRiskAnalysis.editReasonButton')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='space-y-2' aria-busy='true'>
            <Skeleton className='h-4 w-2/3' />
            <Skeleton className='h-4 w-full' />
          </div>
        ) : compliance?.highRiskCheckedAt == null ? (
          <p className='text-sm text-muted-foreground'>
            {t('settings.productMetadata.highRiskAnalysis.notRun')}
          </p>
        ) : compliance.isHighRisk === true &&
          compliance.highRiskUserOverrideReason?.trim() ? (
          <div className='space-y-2 text-sm'>
            <p>
              <span className='font-medium text-foreground'>
                {t('onboarding.highRiskMachine')}:
              </span>{' '}
              {t('common:no')}
            </p>
            <p className='leading-relaxed text-muted-foreground'>
              <span className='font-medium text-foreground'>
                {t('onboarding.highRiskOverride.recordedReasonLabel')}:
              </span>{' '}
              {compliance.highRiskUserOverrideReason.trim()}
            </p>
          </div>
        ) : compliance.isHighRisk === false &&
          compliance.highRiskUserOverrideReason?.trim() ? (
          <div className='space-y-3 text-sm'>
            <div className='rounded-md border border-border bg-muted/30 p-3'>
              <p className='text-xs font-medium tracking-wide text-muted-foreground uppercase'>
                {t('onboarding.userHighRiskOverrideLocked.automatedCheckLabel')}
              </p>
              <p className='mt-1'>
                <span className='font-medium text-foreground'>
                  {t('onboarding.highRiskMachine')}:
                </span>{' '}
                {t('common:no')}
              </p>
              {compliance.highRiskReasoning?.trim() ? (
                <p className='mt-2 leading-relaxed text-muted-foreground'>
                  <span className='font-medium text-foreground'>
                    {t('onboarding.highRiskReasoningLabel')}:
                  </span>{' '}
                  {compliance.highRiskReasoning}
                </p>
              ) : null}
            </div>
            <div className='rounded-md border border-red-200 bg-red-50/80 p-3 dark:border-red-900 dark:bg-red-950/30'>
              <p className='text-xs font-medium tracking-wide text-red-900 uppercase dark:text-red-100'>
                {t(
                  'onboarding.userHighRiskOverrideLocked.yourClassificationLabel'
                )}
              </p>
              <p className='mt-1'>
                <span className='font-medium text-foreground'>
                  {t('onboarding.highRiskMachine')}:
                </span>{' '}
                {t('common:yes')}
              </p>
              <p className='mt-2 leading-relaxed text-muted-foreground'>
                <span className='font-medium text-foreground'>
                  {t('onboarding.highRiskOverride.recordedReasonLabel')}:
                </span>{' '}
                {compliance.highRiskUserOverrideReason.trim()}
              </p>
            </div>
          </div>
        ) : compliance.isHighRisk === false &&
          !compliance.highRiskUserOverrideReason?.trim() ? (
          <div className='space-y-2 text-sm'>
            <p>
              <span className='font-medium text-foreground'>
                {t('onboarding.highRiskMachine')}:
              </span>{' '}
              {t('common:no')}
            </p>
            {compliance.highRiskReasoning?.trim() ? (
              <p className='leading-relaxed text-muted-foreground'>
                <span className='font-medium text-foreground'>
                  {t('onboarding.highRiskReasoningLabel')}:
                </span>{' '}
                {compliance.highRiskReasoning}
              </p>
            ) : null}
          </div>
        ) : compliance.isHighRisk === true ? (
          <div className='space-y-2 text-sm'>
            <p>
              <span className='font-medium text-foreground'>
                {t('onboarding.highRiskMachine')}:
              </span>{' '}
              {t('common:yes')}
            </p>
            {compliance.highRiskCategory ? (
              <p>
                <span className='font-medium text-foreground'>
                  {t('onboarding.highRiskCategoryAnnexIv')}:
                </span>{' '}
                <span>
                  {compliance.highRiskCategory} -{' '}
                  {t(
                    `onboarding.annexIvCategory.${compliance.highRiskCategory}`
                  )}
                </span>
              </p>
            ) : (
              <p className='text-muted-foreground'>
                <span className='font-medium text-foreground'>
                  {t('onboarding.highRiskCategoryAnnexIv')}:
                </span>{' '}
                {t('onboarding.highRiskCategoryNotSpecified')}
              </p>
            )}
            {compliance.highRiskReasoning?.trim() ? (
              <p className='leading-relaxed text-muted-foreground'>
                <span className='font-medium text-foreground'>
                  {t('onboarding.highRiskReasoningLabel')}:
                </span>{' '}
                {compliance.highRiskReasoning}
              </p>
            ) : null}
          </div>
        ) : (
          <p className='text-sm text-muted-foreground'>
            {t('settings.productMetadata.highRiskAnalysis.notRun')}
          </p>
        )}
      </CardContent>
      <ProductMetadataHighRiskReasonDialog
        open={editReasonDialogOpen}
        onOpenChange={setEditReasonDialogOpen}
        isHighRisk={compliance?.isHighRisk}
        storedReason={compliance?.highRiskUserOverrideReason}
        isPending={setHighRiskUserOverrideReasonMutation.isPending}
        onSave={handleSaveHighRiskReason}
      />
    </Card>
  )
}

function ProductMetadataMachineClassificationCard({
  complianceId,
}: {
  complianceId: string | undefined
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { data: compliance, isLoading } =
    useGetMachineryDirectiveCompliance(complianceId)
  const updatePartlyCompletedMachineMutation =
    useUpdatePartlyCompletedMachine(complianceId)
  const [changeDialogOpen, setChangeDialogOpen] = useState(false)

  if (!complianceId) {
    return null
  }

  const classificationNotSet = compliance?.isPartlyCompletedMachine == null
  const isPartlyCompletedMachine = compliance?.isPartlyCompletedMachine === true

  let classificationLabel = t(
    'settings.productMetadata.machineClassification.notSet'
  )
  if (!classificationNotSet) {
    classificationLabel = isPartlyCompletedMachine
      ? t(
          'settings.productMetadata.machineClassification.partlyCompletedMachinery'
        )
      : t('settings.productMetadata.machineClassification.completeMachine')
  }

  const handleSaveClassification = (nextIsPartlyCompletedMachine: boolean) => {
    updatePartlyCompletedMachineMutation.mutate(
      { isPartlyCompletedMachine: nextIsPartlyCompletedMachine },
      {
        onSuccess: () => {
          setChangeDialogOpen(false)
          toast.success(
            t('settings.productMetadata.machineClassification.changeSuccess')
          )
        },
        onError: (error) => {
          toast.error(
            error.message ||
              t('settings.productMetadata.machineClassification.changeFailed')
          )
        },
      }
    )
  }

  return (
    <Card>
      <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-4 space-y-0'>
        <div className='flex w-full justify-between gap-2'>
          <div className='space-y-1'>
            <CardTitle>
              {t('settings.productMetadata.machineClassification.title')}
            </CardTitle>
            <CardDescription>
              {t('settings.productMetadata.machineClassification.description')}
            </CardDescription>
          </div>
          {!classificationNotSet && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setChangeDialogOpen(true)}
              disabled={updatePartlyCompletedMachineMutation.isPending}
            >
              {t('settings.productMetadata.machineClassification.changeButton')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='space-y-2' aria-busy='true'>
            <Skeleton className='h-4 w-2/3' />
          </div>
        ) : (
          <p className='text-sm'>
            <span className='font-medium text-foreground'>
              {t('settings.productMetadata.machineClassification.currentLabel')}
              :
            </span>{' '}
            {classificationLabel}
          </p>
        )}
      </CardContent>
      <ProductMetadataMachineClassificationDialog
        open={changeDialogOpen}
        onOpenChange={setChangeDialogOpen}
        isPartlyCompletedMachine={compliance?.isPartlyCompletedMachine}
        isPending={updatePartlyCompletedMachineMutation.isPending}
        onSave={handleSaveClassification}
      />
    </Card>
  )
}
